import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { Shield, Lock, FileText, AlertCircle, CheckCircle, Info, ExternalLink, Upload, X, Paperclip } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Alert, AlertDescription } from '../components/ui/alert';
import { RichTextDisplay } from '../components/ui/rich-text-display';
import { PrivacyConsent, HoneypotField } from '../components/ui/privacy-consent';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const TITLE_MAX = 100;
const ATTACHMENT_MAX_BYTES = 10 * 1024 * 1024;
const ATTACHMENT_EXTENSIONS = ['.pdf', '.docx', '.jpg', '.jpeg', '.png', '.msg'];

const EMPTY_FORM = {
  report_type: '',
  reporter_role: '',
  is_anonymous: true,
  reporter_first_name: '',
  reporter_last_name: '',
  reporter_phone: '',
  reporter_email: '',
  title: '',
  description: '',
  incident_date: '',
  incident_date_to: '',
  incident_location: '',
  persons_involved: '',
  witnesses: '',
  attachments: [],
};

export const Whistleblowing = () => {
  const [step, setStep] = useState(1);
  const [anonymousCode, setAnonymousCode] = useState('');
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [privacyNotice, setPrivacyNotice] = useState(null);
  const [infoPage, setInfoPage] = useState(null);
  const [taxonomy, setTaxonomy] = useState({ categories: [], roles: [], nominative_available: true });
  const [dateMode, setDateMode] = useState('single');
  const [attachmentFiles, setAttachmentFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [noticeAccepted, setNoticeAccepted] = useState(false);
  const [privacyAccepted, setPrivacyAccepted] = useState(false);
  const [goodFaithAccepted, setGoodFaithAccepted] = useState(false);
  const [honeypot, setHoneypot] = useState('');
  const [noticeExpanded, setNoticeExpanded] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    axios.get(`${API_URL}/api/compliance/whistleblowing`)
      .then((res) => setPrivacyNotice(res.data))
      .catch(() => setPrivacyNotice(null));
    axios.get(`${API_URL}/api/compliance/whistleblowing-info`)
      .then((res) => setInfoPage(res.data))
      .catch(() => setInfoPage(null));
    axios.get(`${API_URL}/api/whistleblowing/categories`)
      .then((res) => setTaxonomy(res.data))
      .catch(() => null);
  }, []);

  const setField = (field, value) => setFormData((prev) => ({ ...prev, [field]: value }));

  const handleFilesSelected = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;

    setUploading(true);
    for (const file of files) {
      const extension = `.${(file.name.split('.').pop() || '').toLowerCase()}`;
      if (!ATTACHMENT_EXTENSIONS.includes(extension)) {
        toast.error(`${file.name}: formato non consentito. Ammessi PDF, DOCX, JPG, PNG, MSG.`);
        continue;
      }
      if (file.size > ATTACHMENT_MAX_BYTES) {
        toast.error(`${file.name}: file troppo grande (max 10 MB).`);
        continue;
      }
      try {
        const body = new FormData();
        body.append('file', file);
        const res = await axios.post(`${API_URL}/api/upload/whistleblowing-attachment`, body);
        setFormData((prev) => ({ ...prev, attachments: [...prev.attachments, res.data.url] }));
        setAttachmentFiles((prev) => [...prev, { name: file.name, size: file.size }]);
        toast.success(`${file.name} allegato`);
      } catch (error) {
        toast.error(error.response?.data?.detail || `Errore nel caricamento di ${file.name}`);
      }
    }
    setUploading(false);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const removeAttachment = (index) => {
    setFormData((prev) => ({ ...prev, attachments: prev.attachments.filter((_, i) => i !== index) }));
    setAttachmentFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const canContinue = formData.report_type && formData.reporter_role &&
    (formData.is_anonymous || (formData.reporter_first_name.trim() && formData.reporter_last_name.trim()));

  const canSubmit = noticeAccepted && privacyAccepted && goodFaithAccepted && !isSubmitting && !uploading;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!goodFaithAccepted) {
      toast.error('Devi rendere la dichiarazione di buona fede per inviare la segnalazione');
      return;
    }
    if (!noticeAccepted || !privacyAccepted) {
      toast.error('Devi dichiarare di aver preso visione delle informative per inviare la segnalazione');
      return;
    }
    setIsSubmitting(true);

    try {
      const payload = {
        ...formData,
        incident_date_to: dateMode === 'range' ? formData.incident_date_to : null,
        good_faith_declared: true,
        privacy_ack: true,
        notice_ack: true,
        website: honeypot,
      };
      const response = await axios.post(`${API_URL}/api/whistleblowing/reports`, payload);

      if (response.data.email_error) {
        toast.warning('Segnalazione salvata, ma l\'email di notifica non è stata inviata. Il team verrà comunque notificato.');
      } else {
        toast.success('Segnalazione inviata con successo!');
      }

      setAnonymousCode(response.data.anonymous_code);
      setStep(3);
    } catch (error) {
      console.error('Error submitting report:', error);
      const errorMessage = error.response?.data?.detail || 'Errore durante l\'invio della segnalazione. Riprova più tardi.';
      toast.error(typeof errorMessage === 'string' ? errorMessage : 'Dati della segnalazione non validi');
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetForm = () => {
    setStep(1);
    setAnonymousCode('');
    setFormData(EMPTY_FORM);
    setAttachmentFiles([]);
    setDateMode('single');
    setNoticeAccepted(false);
    setPrivacyAccepted(false);
    setGoodFaithAccepted(false);
  };

  return (
    <div className="min-h-screen pt-20 bg-background">
      <section className="relative py-16 bg-gradient-dark overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-primary rounded-full blur-3xl"></div>
        </div>
        
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} className="max-w-4xl mx-auto text-center">
            <Shield className="w-16 h-16 text-primary mx-auto mb-6" />
            <h1 className="font-display font-bold text-4xl lg:text-5xl mb-4 text-white">
              Canale Whistleblowing
            </h1>
            <p className="text-lg text-gray-300 mb-4">
              Conforme al Decreto Legislativo 24/2023 e Direttiva UE 1937/2019
            </p>
            <div className="flex flex-wrap justify-center gap-4 text-sm text-gray-300">
              <div className="flex items-center"><Lock className="w-4 h-4 mr-2" />Crittografato</div>
              <div className="flex items-center"><Shield className="w-4 h-4 mr-2" />Anonimo</div>
              <div className="flex items-center"><CheckCircle className="w-4 h-4 mr-2" />Sicuro</div>
            </div>
          </motion.div>
        </div>
      </section>

      <section className="py-12 bg-background">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            {step !== 3 && (
              <Card className="glass-card mb-8" data-testid="whistleblowing-info-section">
                <CardHeader>
                  <CardTitle className="text-2xl">
                    {infoPage?.title || 'Whistleblowing: cos\'è e come segnalare'}
                  </CardTitle>
                  <CardDescription>
                    Leggi queste informazioni prima di compilare il modulo di segnalazione
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {infoPage?.content ? (
                    <RichTextDisplay content={infoPage.content} />
                  ) : (
                    <div className="space-y-3 text-sm text-muted-foreground">
                      <p>
                        Il whistleblowing è lo strumento per segnalare in modo riservato e protetto condotte illecite,
                        irregolarità o violazioni di leggi e procedure aziendali - comprese le violazioni del Modello 231,
                        del Codice Etico o del Codice di Condotta - di cui si sia venuti a conoscenza nel proprio
                        contesto lavorativo.
                      </p>
                      <p>
                        Le segnalazioni non possono riguardare contestazioni o rivendicazioni di carattere personale
                        legate al proprio rapporto di lavoro.
                      </p>
                    </div>
                  )}
                  {infoPage?.pdf_url && (
                    <Button asChild variant="outline" size="sm" className="mt-4">
                      <a href={infoPage.pdf_url} target="_blank" rel="noopener noreferrer" data-testid="whistleblowing-info-pdf">
                        <FileText className="w-4 h-4 mr-2" />
                        Scarica la procedura in PDF
                      </a>
                    </Button>
                  )}
                </CardContent>
              </Card>
            )}

            <Alert className="mb-8 glass-card border-primary/20">
              <Info className="h-5 w-5 text-primary" />
              <AlertDescription className="ml-2">
                <strong>Protezione garantita:</strong> Le tue segnalazioni sono protette. 
                Anonimato garantito come previsto dalle Linee Guida ANAC.
              </AlertDescription>
            </Alert>

            <div className="mb-8">
              <div className="flex items-center justify-center space-x-4">
                {[1, 2, 3].map((s) => (
                  <div key={s} className="flex items-center">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center font-semibold ${
                      step >= s ? 'bg-primary text-white' : 'bg-muted text-muted-foreground'
                    }`}>
                      {s}
                    </div>
                    {s < 3 && <div className={`w-16 h-1 ${step > s ? 'bg-primary' : 'bg-muted'}`}></div>}
                  </div>
                ))}
              </div>
            </div>

            {step === 1 && (
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
                <Card className="glass-card">
                  <CardHeader>
                    <CardTitle>Tipo di Segnalazione</CardTitle>
                    <CardDescription>Seleziona la tipologia e la tua posizione nell'organizzazione</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-6">
                    <div>
                      <Label>Categoria *</Label>
                      <Select value={formData.report_type} onValueChange={(v) => setField('report_type', v)}>
                        <SelectTrigger className="mt-2 bg-background" data-testid="whistleblowing-category-select">
                          <SelectValue placeholder="Seleziona categoria" />
                        </SelectTrigger>
                        <SelectContent className="bg-background">
                          {taxonomy.categories.map((type) => (
                            <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div>
                      <Label>Ruolo / Qualifica nell'organizzazione *</Label>
                      <Select value={formData.reporter_role} onValueChange={(v) => setField('reporter_role', v)}>
                        <SelectTrigger className="mt-2 bg-background" data-testid="whistleblowing-role-select">
                          <SelectValue placeholder="Seleziona il tuo ruolo" />
                        </SelectTrigger>
                        <SelectContent className="bg-background">
                          {taxonomy.roles.map((role) => (
                            <SelectItem key={role.value} value={role.value}>{role.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <p className="text-xs text-muted-foreground mt-1">
                        Serve a verificare l'ambito di applicazione delle tutele di legge ed è richiesto anche nelle
                        segnalazioni anonime.
                      </p>
                    </div>

                    <div>
                      <Label>Modalità *</Label>
                      <RadioGroup value={formData.is_anonymous ? 'anonymous' : 'registered'} onValueChange={(v) => setField('is_anonymous', v === 'anonymous')}>
                        <div className="flex items-center space-x-2 p-4 rounded-lg glass mt-2">
                          <RadioGroupItem value="anonymous" id="anonymous" data-testid="mode-anonymous" />
                          <Label htmlFor="anonymous" className="cursor-pointer flex-1">
                            <div className="font-medium">Segnalazione Anonima</div>
                            <div className="text-sm text-muted-foreground">Nessun dato identificativo viene raccolto</div>
                          </Label>
                        </div>
                        <div className="flex items-center space-x-2 p-4 rounded-lg glass mt-2">
                          <RadioGroupItem value="registered" id="registered" data-testid="mode-nominative" />
                          <Label htmlFor="registered" className="cursor-pointer flex-1">
                            <div className="font-medium">Segnalazione Nominativa (consigliata)</div>
                            <div className="text-sm text-muted-foreground">
                              Identità cifrata, nota solo al gestore del canale e coperta da obbligo di riservatezza
                            </div>
                          </Label>
                        </div>
                      </RadioGroup>
                    </div>

                    {!formData.is_anonymous && (
                      <div className="space-y-4 p-4 rounded-lg bg-muted/30 border border-border" data-testid="nominative-fields">
                        {!taxonomy.nominative_available && (
                          <Alert variant="destructive">
                            <AlertCircle className="h-4 w-4" />
                            <AlertDescription className="ml-2">
                              Il canale nominativo non è disponibile in questo momento: puoi procedere in forma anonima.
                            </AlertDescription>
                          </Alert>
                        )}
                        <div className="grid md:grid-cols-2 gap-4">
                          <div>
                            <Label htmlFor="first_name">Nome *</Label>
                            <Input id="first_name" value={formData.reporter_first_name} onChange={(e) => setField('reporter_first_name', e.target.value)} className="mt-2" data-testid="reporter-first-name" />
                          </div>
                          <div>
                            <Label htmlFor="last_name">Cognome *</Label>
                            <Input id="last_name" value={formData.reporter_last_name} onChange={(e) => setField('reporter_last_name', e.target.value)} className="mt-2" data-testid="reporter-last-name" />
                          </div>
                          <div>
                            <Label htmlFor="phone">Recapito telefonico</Label>
                            <Input id="phone" type="tel" value={formData.reporter_phone} onChange={(e) => setField('reporter_phone', e.target.value)} className="mt-2" data-testid="reporter-phone" />
                          </div>
                          <div>
                            <Label htmlFor="email">Email</Label>
                            <Input id="email" type="email" value={formData.reporter_email} onChange={(e) => setField('reporter_email', e.target.value)} className="mt-2" data-testid="reporter-email" />
                          </div>
                        </div>
                        <p className="text-xs text-muted-foreground">
                          Questi dati sono cifrati a riposo: non compaiono negli elenchi delle segnalazioni né nelle
                          email di notifica e sono consultabili solo dal gestore autorizzato, con registrazione
                          dell'accesso.
                        </p>
                      </div>
                    )}

                    <Button onClick={() => setStep(2)} disabled={!canContinue} className="w-full" size="lg" data-testid="whistleblowing-continue-btn">
                      Continua
                    </Button>
                  </CardContent>
                </Card>
              </motion.div>
            )}

            {step === 2 && (
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
                <Card className="glass-card">
                  <CardHeader>
                    <CardTitle>Dettagli della Segnalazione</CardTitle>
                    <CardDescription>Fornisci quante più informazioni possibile</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <form onSubmit={handleSubmit} className="space-y-4">
                      <div>
                        <div className="flex items-center justify-between">
                          <Label htmlFor="title">Oggetto *</Label>
                          <span className="text-xs text-muted-foreground" data-testid="whistleblowing-title-counter">
                            {formData.title.length}/{TITLE_MAX}
                          </span>
                        </div>
                        <Input
                          id="title"
                          value={formData.title}
                          onChange={(e) => setField('title', e.target.value.slice(0, TITLE_MAX))}
                          maxLength={TITLE_MAX}
                          required
                          className="mt-2"
                          data-testid="whistleblowing-title-input"
                        />
                      </div>

                      <div>
                        <Label htmlFor="description">Descrizione *</Label>
                        <Textarea id="description" value={formData.description} onChange={(e) => setField('description', e.target.value)} required rows={6} className="mt-2 resize-none" data-testid="whistleblowing-description-input" />
                        <p className="text-xs text-muted-foreground mt-1">
                          Indica circostanze di tempo e luogo, persone coinvolte o a conoscenza dei fatti e i documenti a supporto.
                        </p>
                      </div>

                      <div>
                        <Label>Data dell'evento</Label>
                        <RadioGroup value={dateMode} onValueChange={setDateMode} className="flex flex-wrap gap-4 mt-2">
                          <div className="flex items-center space-x-2">
                            <RadioGroupItem value="single" id="date-single" data-testid="date-mode-single" />
                            <Label htmlFor="date-single" className="cursor-pointer text-sm">Data singola</Label>
                          </div>
                          <div className="flex items-center space-x-2">
                            <RadioGroupItem value="range" id="date-range" data-testid="date-mode-range" />
                            <Label htmlFor="date-range" className="cursor-pointer text-sm">Intervallo di date</Label>
                          </div>
                        </RadioGroup>
                        <div className="grid md:grid-cols-2 gap-4 mt-3">
                          <div>
                            <Label htmlFor="incident_date" className="text-sm text-muted-foreground">
                              {dateMode === 'range' ? 'Dal' : 'Data'}
                            </Label>
                            <Input id="incident_date" type="date" value={formData.incident_date} onChange={(e) => setField('incident_date', e.target.value)} className="mt-2" data-testid="incident-date-from" />
                          </div>
                          {dateMode === 'range' && (
                            <div>
                              <Label htmlFor="incident_date_to" className="text-sm text-muted-foreground">Al</Label>
                              <Input id="incident_date_to" type="date" min={formData.incident_date || undefined} value={formData.incident_date_to} onChange={(e) => setField('incident_date_to', e.target.value)} className="mt-2" data-testid="incident-date-to" />
                            </div>
                          )}
                        </div>
                      </div>

                      <div>
                        <Label htmlFor="incident_location">Luogo</Label>
                        <Input id="incident_location" value={formData.incident_location} onChange={(e) => setField('incident_location', e.target.value)} className="mt-2" data-testid="incident-location-input" />
                      </div>

                      <div>
                        <Label htmlFor="persons_involved">Persone Coinvolte</Label>
                        <Textarea id="persons_involved" value={formData.persons_involved} onChange={(e) => setField('persons_involved', e.target.value)} rows={3} className="mt-2 resize-none" data-testid="persons-involved-input" />
                      </div>

                      <div>
                        <Label htmlFor="witnesses">Eventuali Testimoni</Label>
                        <Textarea id="witnesses" value={formData.witnesses} onChange={(e) => setField('witnesses', e.target.value)} rows={2} className="mt-2 resize-none" data-testid="witnesses-input" />
                      </div>

                      <div>
                        <Label>Allegati</Label>
                        <input
                          ref={fileInputRef}
                          type="file"
                          multiple
                          accept=".pdf,.docx,.jpg,.jpeg,.png,.msg"
                          onChange={handleFilesSelected}
                          className="hidden"
                          data-testid="attachment-input"
                        />
                        <div className="mt-2 flex flex-wrap items-center gap-3">
                          <Button type="button" variant="outline" onClick={() => fileInputRef.current?.click()} disabled={uploading} data-testid="attachment-add-btn">
                            <Upload className="w-4 h-4 mr-2" />
                            {uploading ? 'Caricamento...' : 'Aggiungi documenti'}
                          </Button>
                          <span className="text-xs text-muted-foreground">
                            PDF, DOCX, JPG, PNG, MSG - massimo 10 MB per file
                          </span>
                        </div>
                        {attachmentFiles.length > 0 && (
                          <div className="mt-3 space-y-2" data-testid="attachment-list">
                            {attachmentFiles.map((file, idx) => (
                              <div key={`${file.name}-${idx}`} className="flex items-center justify-between gap-3 p-2 rounded-lg bg-muted/40" data-testid={`attachment-item-${idx}`}>
                                <span className="flex items-center gap-2 text-sm truncate">
                                  <Paperclip className="w-4 h-4 text-muted-foreground shrink-0" />
                                  <span className="truncate">{file.name}</span>
                                  <span className="text-xs text-muted-foreground shrink-0">
                                    {(file.size / (1024 * 1024)).toFixed(1)} MB
                                  </span>
                                </span>
                                <Button type="button" variant="ghost" size="icon" onClick={() => removeAttachment(idx)} data-testid={`remove-attachment-${idx}`}>
                                  <X className="w-4 h-4" />
                                </Button>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>

                      <div className="pt-4 border-t border-border" data-testid="whistleblowing-privacy-notice">
                        <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
                          <h4 className="font-semibold flex items-center gap-2">
                            <Info className="w-4 h-4 text-primary" />
                            {privacyNotice?.title || 'Informativa Whistleblowing'}
                          </h4>
                          <div className="flex flex-wrap items-center gap-1">
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={() => setNoticeExpanded(!noticeExpanded)}
                              data-testid="toggle-privacy-notice"
                            >
                              {noticeExpanded ? 'Comprimi' : 'Leggi tutto'}
                            </Button>
                            <Button asChild type="button" variant="ghost" size="sm">
                              <a
                                href="/compliance/whistleblowing"
                                target="_blank"
                                rel="noopener noreferrer"
                                data-testid="whistleblowing-notice-link"
                              >
                                Apri pagina
                                <ExternalLink className="w-3.5 h-3.5 ml-1" />
                              </a>
                            </Button>
                          </div>
                        </div>
                        <div
                          className={`rounded-lg bg-muted/40 p-4 overflow-y-auto ${noticeExpanded ? 'max-h-[60vh]' : 'max-h-56'}`}
                        >
                          {privacyNotice?.content ? (
                            <RichTextDisplay content={privacyNotice.content} className="prose-sm" />
                          ) : (
                            <p className="text-sm text-muted-foreground">Caricamento informativa...</p>
                          )}
                        </div>
                        <label className="mt-4 flex items-start gap-3 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={noticeAccepted}
                            onChange={(e) => setNoticeAccepted(e.target.checked)}
                            className="mt-1 h-4 w-4 accent-primary focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
                            data-testid="whistleblowing-notice-checkbox"
                          />
                          <span className="text-sm text-muted-foreground">
                            Ho preso visione dell'
                            <a
                              href="/compliance/whistleblowing"
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-primary hover:underline"
                            >
                              informativa sul trattamento dei dati del canale whistleblowing
                            </a>{' '}
                            (obbligatorio) *
                          </span>
                        </label>
                      </div>

                      <div className="p-4 rounded-lg bg-muted/30 border border-border" data-testid="good-faith-declaration">
                        <label className="flex items-start gap-3 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={goodFaithAccepted}
                            onChange={(e) => setGoodFaithAccepted(e.target.checked)}
                            className="mt-1 h-4 w-4 accent-primary focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
                            data-testid="good-faith-checkbox"
                          />
                          <span className="text-sm leading-relaxed">
                            Dichiaro di avere fondato motivo di ritenere vere le informazioni riportate e di agire in
                            buona fede, consapevole che le segnalazioni palesemente false o diffamatorie comportano
                            responsabilità disciplinari e sanzioni a norma di legge. *
                          </span>
                        </label>
                      </div>

                      <PrivacyConsent
                        checked={privacyAccepted}
                        onChange={setPrivacyAccepted}
                        testId="whistleblowing-privacy-checkbox"
                        note="* Campo obbligatorio per l'invio della segnalazione"
                      />

                      <HoneypotField value={honeypot} onChange={setHoneypot} idPrefix="wb" />

                      <div className="flex space-x-4 pt-4">
                        <Button type="button" variant="outline" onClick={() => setStep(1)} className="flex-1">
                          Indietro
                        </Button>
                        <Button type="submit" disabled={!canSubmit} className="flex-1" data-testid="submit-report-btn">
                          {isSubmitting ? 'Invio...' : 'Invia Segnalazione'}
                        </Button>
                      </div>
                    </form>
                  </CardContent>
                </Card>
              </motion.div>
            )}

            {step === 3 && (
              <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}>
                <Card className="glass-card border-primary/20">
                  <CardContent className="pt-6 text-center">
                    <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-6">
                      <CheckCircle className="w-8 h-8 text-primary" />
                    </div>
                    <h3 className="font-display font-bold text-2xl mb-4">Segnalazione Inviata</h3>
                    <p className="text-muted-foreground mb-6">
                      La tua segnalazione è stata ricevuta
                    </p>

                    <Alert className="mb-6 text-left">
                      <AlertCircle className="h-5 w-5 text-primary" />
                      <AlertDescription className="ml-2">
                        <strong className="block mb-2">Codice Anonimo:</strong>
                        <code className="block bg-muted p-4 rounded-lg font-mono text-lg text-primary select-all">
                          {anonymousCode}
                        </code>
                        <p className="mt-3 text-sm">
                          Conserva questo codice per tracciare la tua segnalazione
                        </p>
                      </AlertDescription>
                    </Alert>

                    <div className="space-y-3">
                      <Button onClick={resetForm} variant="outline" className="w-full">
                        Nuova Segnalazione
                      </Button>
                      <Button onClick={() => window.location.href = '/whistleblowing/track'} className="w-full">
                        Traccia Segnalazione
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            )}
          </div>
        </div>
      </section>

      <section className="py-16 bg-muted/50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <h2 className="font-display font-bold text-3xl mb-8 text-center">Garanzie di Protezione</h2>
            <div className="grid md:grid-cols-3 gap-6">
              <Card className="glass-card">
                <CardContent className="pt-6 text-center">
                  <Lock className="w-12 h-12 text-primary mx-auto mb-4" />
                  <h3 className="font-semibold mb-2">Identità cifrata</h3>
                  <p className="text-sm text-muted-foreground">
                    Dati identificativi cifrati a riposo e accessi registrati
                  </p>
                </CardContent>
              </Card>
              <Card className="glass-card">
                <CardContent className="pt-6 text-center">
                  <Shield className="w-12 h-12 text-primary mx-auto mb-4" />
                  <h3 className="font-semibold mb-2">Divieto di ritorsione</h3>
                  <p className="text-sm text-muted-foreground">
                    Tutele di legge per segnalanti, facilitatori e colleghi
                  </p>
                </CardContent>
              </Card>
              <Card className="glass-card">
                <CardContent className="pt-6 text-center">
                  <FileText className="w-12 h-12 text-primary mx-auto mb-4" />
                  <h3 className="font-semibold mb-2">Conformità Normativa</h3>
                  <p className="text-sm text-muted-foreground">
                    Conforme D.Lgs 24/2023 e procedure ISO 37001
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Whistleblowing;
